import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * SchemaMarkup component for adding structured data to pages
 * This helps search engines understand the content and display rich snippets in search results
 */
const SchemaMarkup = ({ 
  type = 'Organization', 
  name = 'Prime Edge',
  description,
  url,
  imageUrl,
  courseData = null,
  articleData = null,
  breadcrumbData = null
}) => {
  // Base organization schema that will be used across the site
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Prime Edge',
    url: 'https://primeedge-solutions.com',
    logo: 'https://primeedge-solutions.com/logo.png',
    sameAs: [
      'https://www.facebook.com/primeedge',
      'https://www.twitter.com/primeedge',
      'https://www.linkedin.com/company/primeedge'
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+1-555-555-5555',
      contactType: 'customer service',
      email: 'info@primeedge.com'
    }
  };

  // Website schema
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Prime Edge',
    url: 'https://primeedge-solutions.com',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://primeedge-solutions.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string'
    }
  };

  // Generate schema based on page type
  const generateSchema = () => {
    // Default to organization schema
    let schema = organizationSchema;

    // Add website schema for all pages
    let schemas = [organizationSchema, websiteSchema];

    // Add page-specific schema based on type
    if (type === 'Course' && courseData) {
      const courseSchema = {
        '@context': 'https://schema.org',
        '@type': 'Course',
        name: courseData.title,
        description: courseData.description,
        provider: {
          '@type': 'Organization',
          name: 'Prime Edge',
          sameAs: 'https://primeedge-solutions.com'
        },
        url: url || `https://primeedge-solutions.com/courses/${courseData.slug}`,
        ...(courseData.image && { image: courseData.image }),
        ...(courseData.instructor && {
          instructor: {
            '@type': 'Person',
            name: `${courseData.instructor.firstName} ${courseData.instructor.lastName}`,
          }
        })
      };
      schemas.push(courseSchema);
    } else if (type === 'Article' && articleData) {
      const articleSchema = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: articleData.title,
        description: articleData.description,
        image: articleData.image,
        datePublished: articleData.datePublished,
        dateModified: articleData.dateModified,
        author: {
          '@type': 'Person',
          name: articleData.author,
        },
        publisher: {
          '@type': 'Organization',
          name: 'Prime Edge',
          logo: {
            '@type': 'ImageObject',
            url: 'https://primeedge-solutions.com/logo.png'
          }
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': url || `https://primeedge-solutions.com/blog/${articleData.slug}`
        }
      };
      schemas.push(articleSchema);
    }

    // Add breadcrumb schema if provided
    if (breadcrumbData && breadcrumbData.length > 0) {
      const breadcrumbSchema = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbData.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: item.url
        }))
      };
      schemas.push(breadcrumbSchema);
    }

    return schemas;
  };

  const schemas = generateSchema();

  return (
    <Helmet>
      {schemas.map((schema, index) => (
        <script key={`schema-${index}`} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SchemaMarkup;